import { useAuth } from '@teko/auth-react';
import { RatingFilled } from '@teko/ds-icon';
import {
  Body,
  Button,
  Col,
  Divider,
  Flex,
  Heading,
  LazyImage,
  Loading,
  Row,
  Shape,
  Skeleton,
  styled,
  themeColor,
  useModal,
} from '@teko/ds-mobile';
import NoRatingImage from 'assets/product-no-rating.svg';
import { ProgressBar } from 'components/ProgressBar';
import { useTenantConfig } from 'contexts';
import { t } from 'i18n';
import React, { useMemo, useState } from 'react';
import { IRatingData } from 'types';
import { ProductReview } from './ProductReview';
import { RatingSuccessModal } from './RatingSuccessModal';
import { StarRating } from './StarRating';

interface IRatingOverviewProps {
  ratingData: IRatingData;
}

export const RatingOverview: React.FC<IRatingOverviewProps> = ({
  ratingData,
}) => {
  const { isMobile, epicOmni1550HideReviewButton } = useTenantConfig();
  const { showModal } = useModal();
  const { user, authService } = useAuth();

  const [showProductReview, setShowProductReview] = useState(false);
  const [showRatingSuccessModal, setShowRatingSuccessModal] = useState(false);
  const {
    averageRating,
    totalReviews,
    ratingCounts,
    setFilterParams,
    setPage,
    refreshReviewList,
  } = ratingData;

  const sortedRatingCounts = useMemo(() => {
    return ratingCounts?.sort((a, b) => b.star - a.star);
  }, [ratingCounts]);

  const handleUnauthenticatedUser = () => {
    showModal({
      size: 'm',
      title: t('Thông báo'),
      centeredTitle: true,
      okText: t('Đăng nhập'),
      cancelText: t('Đóng'),
      children: (
        <Flex direction="column" align="center">
          <Body marginTop="s4">
            {t('Vui lòng đăng nhập để đánh giá sản phẩm.')}
          </Body>
        </Flex>
      ),
      alignFooter: 'center',
      onOk: () => authService.login(),
    });
  };

  const writeProductReview = () => {
    if (!user?.userId) {
      handleUnauthenticatedUser();
      return;
    }
    setShowProductReview(true);
  };

  return (
    <>
      <Shape padding="s16" backgroundColor="white">
        {ratingData.isValidating ? (
          <Loading isLoading size={40} />
        ) : !!totalReviews ? (
          <Row justify="center" align="middle">
            <Col
              span={!isMobile ? 2 : 12}
              style={{
                alignItems: 'center',
              }}
            >
              <Row align="middle">
                <Col
                  span={!isMobile ? 12 : 6}
                  style={{
                    alignItems: 'center',
                    justifyItems: !isMobile ? 'center' : 'start',
                  }}
                >
                  <Heading
                    marginRight="s4"
                    weight="bold"
                    style={{ fontSize: '40px', lineHeight: '56px' }}
                  >
                    {averageRating}/5
                  </Heading>

                  <StarRating
                    value={averageRating || 0}
                    starSize={20}
                    gap={8}
                  />

                  <Body marginTop="s8" weight="regular">
                    {totalReviews} {t('lượt đánh giá')}
                  </Body>
                </Col>
                <Col
                  span={!isMobile ? 12 : 6}
                  style={{
                    display: 'flex',
                    justifyContent: !isMobile ? 'center' : 'end',
                  }}
                  marginTop="s8"
                >
                  {!epicOmni1550HideReviewButton && (
                    <WriteReviewButton
                      variant="primary"
                      borderRadius="m"
                      onClick={writeProductReview}
                    >
                      {t('Viết đánh giá')}
                    </WriteReviewButton>
                  )}
                </Col>
              </Row>
            </Col>

            {!isMobile ? (
              <Divider color="divider" orientation="vertical" width="100px" />
            ) : (
              <Col span={12}>
                <Divider color="divider" orientation="horizontal" />
              </Col>
            )}
            {sortedRatingCounts ? (
              <Col span={!isMobile ? 8 : 12}>
                {sortedRatingCounts.map((item) => {
                  const percent =
                    totalReviews > 0 ? (item.count / totalReviews) * 100 : 0;

                  return (
                    <MemoRatingItem
                      key={item.star}
                      percent={percent}
                      star={item.star}
                      count={item.count}
                    />
                  );
                })}
              </Col>
            ) : (
              <Skeleton height="0.75rem" width="50%" />
            )}
          </Row>
        ) : (
          !epicOmni1550HideReviewButton && (
            <Flex direction="column" align="center" gap="s16">
              <LazyImage src={NoRatingImage} width={160} height={93} />
              <Body
                weight={isMobile ? 'regular' : 'medium'}
                style={{ fontSize: isMobile ? 13 : 15 }}
              >
                {t('Hãy trở thành người đầu tiên đánh giá sản phẩm này')}
              </Body>
              <WriteReviewButton
                variant="primary"
                borderRadius="m"
                onClick={writeProductReview}
              >
                {t('Viết đánh giá')}
              </WriteReviewButton>
            </Flex>
          )
        )}
      </Shape>
      {!!showProductReview && (
        <ProductReview
          visible={showProductReview}
          onCloseModal={() => setShowProductReview(false)}
          openSuccessModal={() => setShowRatingSuccessModal(true)}
          refetchReviewList={() => {
            setFilterParams(undefined);
            setPage(1);
            refreshReviewList();
          }}
        />
      )}
      {!!showRatingSuccessModal && (
        <RatingSuccessModal
          show={showRatingSuccessModal}
          onClose={() => setShowRatingSuccessModal(false)}
        />
      )}
    </>
  );
};

const WriteReviewButton = styled(Button)`
  .button-text {
    font-weight: 500;
    font-size: 15px;
    line-height: 24px;
  }
`;

interface IRatingItemProps {
  percent: number;
  star: number;
  count: number;
}

const RatingItem = ({ percent, star, count }: IRatingItemProps) => {
  const { isMobile } = useTenantConfig();

  return (
    <Row key={star} align="middle" justify="start" margin={['s8', 's0']}>
      <Col span={1} marginRight="s8">
        <Row align="middle" justify="end">
          <Body marginRight="s4" weight="medium">
            {star}
          </Body>
          <RatingFilled size={14} color="support" />
        </Row>
      </Col>
      <Col span={!isMobile ? 7 : 6}>
        <ProgressBar
          barHeight={8}
          progress={percent}
          backgroundColor={themeColor.divider}
          barColor="#0300be"
        />
      </Col>
      <Col span={!isMobile ? 3.5 : 4} marginLeft="s8">
        <Body weight="regular">
          {count} {t('lượt đánh giá')}
        </Body>
      </Col>
    </Row>
  );
};

const MemoRatingItem = React.memo(
  RatingItem,
  (prev: IRatingItemProps, next: IRatingItemProps) =>
    prev.count === next.count &&
    prev.percent === next.percent &&
    prev.star === prev.star,
);
